import type { Breakpoint } from "@mui/material";
import type React from "react";
import { type CSSProperties, memo, useEffect } from "react";
import type { Department } from "../../contexts/AuthProvider";
import { useRoleColors } from "../../hooks/useRoleColors";
import { trackEvent } from "../../utils/analytics";
import type { NurseFullRole, NurseRole } from "../../utils/models";
import { nurseRoleDisplayName, rolesMap } from "../../utils/utilFunctions";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";

interface RoleSelectorProps {
	onRolesChange: (selectedRoles: NurseFullRole[]) => void;
	chosenDepartment: Department;
	isHorizontal?: boolean;
	/** The roles to offer — the page passes them so it can offer another department's (the ops backoffice drill-down). */
	roles: NurseFullRole[];
	selectedRoles: NurseFullRole[];
	width?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
}

const RoleSelector: React.FC<RoleSelectorProps> = memo(({ onRolesChange, chosenDepartment, roles, selectedRoles, width }) => {
	const roleColor = useRoleColors();

	const defaultRoles = Object.entries(rolesMap)
		.filter(([, fullRole]) => roles.includes(fullRole))
		.map(([role, fullRole]) => ({
			label: (nurseRoleDisplayName[role as NurseRole] ?? role) as NurseRole,
			value: fullRole as NurseFullRole,
			color: roleColor(fullRole).dot,
		}));

	useEffect(() => {
		trackEvent("role_selector_mounted", {
			department_id: chosenDepartment?.department_id,
			available_roles: roles.length,
		});
	}, []);

	const handleContractTypeChange = (value: NurseFullRole[]) => {
		trackEvent("role_selector_changed", {
			department_id: chosenDepartment?.department_id,
			previous_selection: selectedRoles.join(","),
			new_selection: value.join(","),
		});

		onRolesChange(value);
	};

	return (
		<div className="flex flex-col space-y-2" style={{ display: "flex" }}>
			<SystemDropdown
				label={"Roles"}
				fullWidth
				multiple
				items={defaultRoles}
				selectedItems={selectedRoles}
				onChangeMultiple={(value) => handleContractTypeChange(value)}
				selectSx={{ width: width ?? "200px" }}
				renderAllValueText="All"
			/>
		</div>
	);
});

export default RoleSelector;
